import React, { FC, useState } from 'react';
import {
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Chip,
  Box,
} from '@material-ui/core';
import { AnalyticsFilters, MeasurementFilter, CheckTypeName, CHECK_TYPE_LABEL } from '../../constants/Analytics';
import { UserGenderMap, UserCareLevelMap } from '../../constants/User';

interface Props {
  filters: AnalyticsFilters;
  onSearch: (f: MeasurementFilter) => void;
}

const genderLabel = (v: string) => UserGenderMap[Number(v)] ?? v;
const careLabel = (v: string) => UserCareLevelMap[Number(v)] ?? v;

const FilterPanel: FC<Props> = ({ filters, onSearch }) => {
  const [checkType, setCheckType] = useState<CheckTypeName>('tug');
  const [companyNames, setCompanyNames] = useState<string[]>([]);
  const [institutionSubs, setInstitutionSubs] = useState<string[]>([]);
  const [genders, setGenders] = useState<string[]>([]);
  const [careLevels, setCareLevels] = useState<string[]>([]);
  const [startedFrom, setStartedFrom] = useState('');
  const [startedTo, setStartedTo] = useState('');
  const [valueMin, setValueMin] = useState('');
  const [valueMax, setValueMax] = useState('');

  const build = (): MeasurementFilter => {
    const f: MeasurementFilter = { check_type: checkType };
    if (companyNames.length) f.company_names = companyNames;
    if (institutionSubs.length) f.institution_subs = institutionSubs;
    if (genders.length) f.genders = genders;
    if (careLevels.length) f.care_levels = careLevels;
    if (startedFrom) f.started_from = startedFrom;
    if (startedTo) f.started_to = startedTo;
    const min = valueMin === '' ? undefined : Number(valueMin);
    const max = valueMax === '' ? undefined : Number(valueMax);
    if (min !== undefined || max !== undefined) f.value_range = { min, max };
    return f;
  };

  return (
    <Paper style={{ padding: 16, marginBottom: 16 }}>
      <Grid container spacing={2}>
        <Grid item xs={6} sm={3}>
          <FormControl fullWidth>
            <InputLabel>種目</InputLabel>
            <Select value={checkType} onChange={e => setCheckType(e.target.value as CheckTypeName)}>
              {(['tug', 'cs30', 'ss5', 'balance'] as CheckTypeName[]).map(t => (
                <MenuItem key={t} value={t}>
                  {CHECK_TYPE_LABEL[t]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6} sm={3}>
          <FormControl fullWidth>
            <InputLabel>法人</InputLabel>
            <Select
              multiple
              value={companyNames}
              onChange={e => setCompanyNames(e.target.value as string[])}
              renderValue={sel => (
                <Box>
                  {(sel as string[]).map(v => (
                    <Chip key={v} label={v} size="small" />
                  ))}
                </Box>
              )}
            >
              {filters.company_names.map(v => (
                <MenuItem key={v} value={v}>
                  {v}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6} sm={3}>
          <FormControl fullWidth>
            <InputLabel>施設</InputLabel>
            <Select
              multiple
              value={institutionSubs}
              onChange={e => setInstitutionSubs(e.target.value as string[])}
              renderValue={sel => (
                <Box>
                  {(sel as string[]).map(v => {
                    const inst = filters.institutions.find(i => i.institution_sub === v);
                    return <Chip key={v} label={inst ? inst.institution_name : v} size="small" />;
                  })}
                </Box>
              )}
            >
              {filters.institutions.map(i => (
                <MenuItem key={i.institution_sub} value={i.institution_sub}>
                  {i.institution_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6} sm={3}>
          <FormControl fullWidth>
            <InputLabel>性別</InputLabel>
            <Select
              multiple
              value={genders}
              onChange={e => setGenders(e.target.value as string[])}
              renderValue={sel => (sel as string[]).map(genderLabel).join(', ')}
            >
              {filters.genders.map(v => (
                <MenuItem key={v} value={v}>
                  {genderLabel(v)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6} sm={3}>
          <FormControl fullWidth>
            <InputLabel>要介護度</InputLabel>
            <Select
              multiple
              value={careLevels}
              onChange={e => setCareLevels(e.target.value as string[])}
              renderValue={sel => (sel as string[]).map(careLabel).join(', ')}
            >
              {filters.care_levels.map(v => (
                <MenuItem key={v} value={v}>
                  {careLabel(v)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField
            label="開始日"
            type="date"
            value={startedFrom}
            onChange={e => setStartedFrom(e.target.value)}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField
            label="終了日"
            type="date"
            value={startedTo}
            onChange={e => setStartedTo(e.target.value)}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField
            label="値 最小"
            type="number"
            value={valueMin}
            onChange={e => setValueMin(e.target.value)}
            fullWidth
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <TextField
            label="値 最大"
            type="number"
            value={valueMax}
            onChange={e => setValueMax(e.target.value)}
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <Button color="primary" variant="contained" onClick={() => onSearch(build())}>
            検索
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default FilterPanel;
